import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { rupee, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import CustomerLayout from "@/components/CustomerLayout";
import AddressForm from "@/components/AddressForm";
import RecoRow from "@/components/RecoRow";
import { EmptyState, Spinner } from "@/components/States";
import { loadRazorpay } from "@/lib/razorpay";
import { MapPin, Tag, Wallet, Smartphone, CreditCard, Plus, ShoppingBag, CheckCircle2, Truck, Gift } from "lucide-react";
import { toast } from "sonner";

const PAYMENTS = [
  { v: "upi", label: "UPI", sub: "GPay, PhonePe, Paytm", icon: Smartphone, testid: "checkout-payment-upi" },
  { v: "card", label: "Card", sub: "Debit / Credit", icon: CreditCard, testid: "checkout-payment-card" },
  { v: "cod", label: "Cash on Delivery", sub: "Pay when it arrives", icon: Wallet, testid: "checkout-payment-cod" },
];

export default function Checkout() {
  const nav = useNavigate();
  const { cart, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [payment, setPayment] = useState("upi");
  const [coupon, setCoupon] = useState("");
  const [pricing, setPricing] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [showAddr, setShowAddr] = useState(false);
  const [eligible, setEligible] = useState([]);

  const loadAddrs = () => api.get("/addresses").then((r) => {
    setAddresses(r.data);
    const def = r.data.find((a) => a.is_default) || r.data[0];
    if (def) setSelectedAddr(def);
    setShowAddr(r.data.length === 0);
  });
  useEffect(() => { loadAddrs(); }, []);

  useEffect(() => {
    if (!cart.items.length) return;
    api.post("/cart/price", { store_id: cart.store.id, items: cart.items, address: {}, coupon_code: coupon || null })
      .then((r) => setPricing(r.data)).catch(() => {});
    api.get("/coupons/eligible", { params: { store_id: cart.store.id, subtotal } })
      .then((r) => setEligible(r.data)).catch(() => {});
  }, [cart, coupon, subtotal]);

  const applyCoupon = async (code) => {
    const c = (code || coupon || "").toUpperCase();
    if (!c) return;
    try { await api.post("/coupons/validate", { code: c, subtotal, store_id: cart.store.id }); setCoupon(c); toast.success("Coupon applied!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveAddress = async (addr) => {
    const { data } = await api.post("/addresses", addr);
    setAddresses((prev) => [...prev, data]); setSelectedAddr(data); setShowAddr(false);
    toast.success("Address saved");
  };

  const placeOrder = async () => {
    if (!selectedAddr) return toast.error("Please select a delivery address");
    const { id, user_id, _id, created_at, ...address } = selectedAddr;
    setPlacing(true);
    try {
      const affiliate_code = localStorage.getItem("gf_ref") || null;
      if (payment === "cod") {
        const { data } = await api.post("/orders", { store_id: cart.store.id, items: cart.items, address, payment_method: "cod", coupon_code: pricing?.coupon || null, affiliate_code });
        clear(); toast.success(`Order ${data.order_no} placed!`); nav(`/order/${data.id}`); return;
      }
      const { data: init } = await api.post("/payments/checkout/create", { store_id: cart.store.id, items: cart.items, address, coupon_code: pricing?.coupon || null });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: cart.store.name,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone }, theme: { color: "#059669" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/payments/checkout/verify", { ...resp, store_id: cart.store.id, items: cart.items, address, coupon_code: pricing?.coupon || null, affiliate_code });
            clear(); toast.success("Payment successful!"); nav(`/order/${data.id}`);
          } catch (e) { toast.error(apiError(e)); }
        },
        modal: { ondismiss: () => { setPlacing(false); toast.error("Payment cancelled"); } },
      });
      rzp.open(); return;
    } catch (e) { toast.error(apiError(e)); }
    setPlacing(false);
  };

  if (!cart.items.length) return (
    <CustomerLayout><EmptyState icon={ShoppingBag} title="Your cart is empty" subtitle="Add items before checking out." action={<button onClick={() => nav("/stores")} className="mt-4 rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white">Browse stores</button>} /></CustomerLayout>
  );

  const toFree = pricing?.amount_to_free ?? 0;
  const threshold = pricing?.free_delivery_threshold ?? 0;
  const pct = threshold ? Math.min(100, Math.round((pricing.subtotal / threshold) * 100)) : 100;

  return (
    <CustomerLayout>
      <h1 className="mb-5 font-head text-2xl font-extrabold text-slate-900 sm:text-3xl">Checkout</h1>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="mb-3 flex items-center gap-2 font-head font-bold text-slate-900"><MapPin className="h-5 w-5 text-brand" /> Delivery address</h2>
            <div className="space-y-2" data-testid="checkout-address-select">
              {addresses.map((a) => (
                <button key={a.id} onClick={() => setSelectedAddr(a)}
                  className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left ${selectedAddr?.id === a.id ? "border-brand bg-brand-soft" : "border-slate-200"}`}>
                  <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${selectedAddr?.id === a.id ? "border-brand bg-brand" : "border-slate-300"}`} />
                  <div><p className="text-sm font-semibold text-slate-800">{a.label} {a.is_default && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">DEFAULT</span>}</p><p className="text-xs text-slate-500">{a.line}, {a.city} {a.state} {a.pincode}</p></div>
                </button>
              ))}
            </div>
            {showAddr ? (
              <div className="mt-3"><AddressForm onSubmit={saveAddress} onCancel={addresses.length ? () => setShowAddr(false) : null} /></div>
            ) : (
              <button data-testid="checkout-address-add-new" onClick={() => setShowAddr(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"><Plus className="h-4 w-4" /> Add new address</button>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="mb-3 flex items-center gap-2 font-head font-bold text-slate-900"><Wallet className="h-5 w-5 text-brand" /> Payment method</h2>
            <div className="space-y-2">
              {PAYMENTS.map((p) => (
                <button key={p.v} data-testid={p.testid} onClick={() => setPayment(p.v)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${payment === p.v ? "border-brand bg-brand-soft" : "border-slate-200"}`}>
                  <p.icon className="h-5 w-5 text-slate-600" />
                  <div className="flex-1"><p className="text-sm font-semibold text-slate-800">{p.label}</p><p className="text-xs text-slate-500">{p.sub}</p></div>
                  {payment === p.v && <CheckCircle2 className="h-5 w-5 text-brand" />}
                </button>
              ))}
            </div>
            {payment !== "cod" && <p className="mt-2 rounded-lg bg-amber-soft px-3 py-2 text-xs text-amber-warm">You will be securely charged via Razorpay. Real payment.</p>}
          </section>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-head font-bold text-slate-900">Order summary</h2>
            <p className="text-xs text-slate-500">{cart.items.length} items from {cart.store?.name}</p>

            {pricing && (toFree > 0 ? (
              <div data-testid="free-delivery-progress" className="rounded-xl bg-brand-soft/60 p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-brand"><Truck className="h-4 w-4" /> Add {rupee(toFree)} more for FREE delivery</p>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} /></div>
              </div>
            ) : (
              <p data-testid="free-delivery-unlocked" className="rounded-xl bg-brand-soft/60 p-3 text-xs font-bold text-brand">🎉 You've unlocked FREE delivery</p>
            ))}

            {eligible.length > 0 && (
              <div className="space-y-1.5" data-testid="coupon-recommendations">
                {eligible.slice(0, 2).map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl border border-dashed border-brand/40 bg-white px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-brand" />
                      <div><p className="font-mono text-xs font-bold text-brand">{c.code}</p><p className="text-[11px] text-slate-500">{c.discount_type === "fixed" ? `Save ${rupee(c.value)}` : `${c.value}% off`}{c.min_order ? ` · min ${rupee(c.min_order)}` : ""}</p></div>
                    </div>
                    <button onClick={() => applyCoupon(c.code)} data-testid={`apply-coupon-${c.code}`} className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white">Apply</button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter coupon code" data-testid="checkout-coupon-input" className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-16 text-sm outline-none focus:border-brand" />
              <button onClick={() => applyCoupon()} data-testid="checkout-apply-coupon-btn" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">Apply</button>
            </div>

            {pricing ? (
              <div className="space-y-2 text-sm">
                <Row label="Subtotal" value={rupee(pricing.subtotal)} />
                <Row label="Delivery fee" value={pricing.delivery_fee === 0 ? "FREE" : rupee(pricing.delivery_fee)} />
                <Row label="Platform fee" value={rupee(pricing.platform_fee)} />
                <Row label="Taxes" value={rupee(pricing.tax)} />
                {pricing.discount > 0 && <Row label={`Discount (${pricing.coupon})`} value={`- ${rupee(pricing.discount)}`} green />}
                {pricing.membership_discount > 0 && <div data-testid="checkout-membership-savings"><Row label="Grofkart Daily savings" value={`- ${rupee(pricing.membership_discount)}`} green /></div>}
                <div className="flex items-center justify-between border-t border-dashed border-slate-200 pt-2">
                  <span className="font-head font-bold">To pay</span>
                  <span data-testid="checkout-total-amount" className="font-mono text-lg font-bold text-brand">{rupee(pricing.total)}</span>
                </div>
              </div>
            ) : <div className="flex justify-center py-4"><Spinner /></div>}
            <button data-testid="checkout-place-order-btn" onClick={placeOrder} disabled={placing}
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-base font-bold text-white hover:bg-brand-deep disabled:opacity-60">
              {placing && <Spinner className="h-4 w-4 border-white" />} Place order
            </button>
          </div>
        </div>
      </div>

      <RecoRow title="Complete your order" storeId={cart.store?.id} exclude={cart.items.map((i) => i.product_id).join(",")} store={cart.store} />
    </CustomerLayout>
  );
}

const Row = ({ label, value, green }) => (
  <div className="flex items-center justify-between text-slate-600"><span>{label}</span><span className={`font-mono font-medium ${green ? "text-brand" : "text-slate-800"}`}>{value}</span></div>
);
